import { AutoNavigate, StepProps } from '@betterment/js-runtime';
import { H1, Icon, ListRow, Text, VStack } from '@betterment/samba-react';
import { ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';

import { trackClick } from '../../../features/track';
import FullPageLoader from '../../../Shared/components/FullPageLoader';
import { useDirectTradingAccountInflowOptions } from '../../../Shared/hooks/useDirectTradingAccountInflowOptions/useDirectTradingAccountInflowOptions';
import { WorkflowLayout } from '../../../Shared/layout/WorkflowLayout';
import { locationAssign } from '../../../Shared/path-helpers';
import { urlBuilder } from '../../../Shared/urlBuilder';

const linkCss = {
  color: 'text',
  width: '100%',
  _hover: { color: 'text', textDecoration: 'underline', cursor: 'pointer' },
};
const listRowCss = {
  border: 'token(colors.border) solid token(borderWidths.thin)',
  padding: 'space.200',
  borderRadius: 'medium',
};
const iconCss = {
  backgroundColor: 'background.neutral',
  display: 'flex',
  borderRadius: 'medium',
  padding: 'space.100',
};

const Content: React.FC<{ accountId: string }> = ({ accountId }) => {
  const options = useDirectTradingAccountInflowOptions({ accountId, throwOnError: true });
  const accountOverviewUrl = urlBuilder.appAccount.absolute({ accountId });

  if (!options) return <FullPageLoader />;

  const { depositOption, transferOption } = options;

  const returnTo = accountOverviewUrl;
  const subAccountId = depositOption.destinationLegacySubAccountId;
  const depositSetupUrl = urlBuilder.appDepositSetup.absolute({ subAccountId, returnTo });

  if (!transferOption) {
    return <AutoNavigate to={depositSetupUrl} replace={true} />;
  }

  const transferSetupUrl = urlBuilder.showSubAccountTransferSetup.absolute({
    subAccountId: transferOption.sourceLegacySubAccountId,
    destination_account_id: transferOption.destinationLegacySubAccountId,
    returnTo,
  });

  return (
    <VStack css={{ gap: 'space.300', paddingY: 'space.400' }}>
      <H1 variant="title400">Choose how to fund your account.</H1>
      <Text>Add cash so you're ready to trade.</Text>
      <SambaAutoLink
        to={depositSetupUrl}
        css={linkCss}
        onClick={() => {
          trackClick({ name: 'Deposit', flow: 'FundAccount' });
        }}
      >
        <ListRow css={listRowCss}>
          <ListRow.LeadingAsset css={iconCss}>
            <Icon name="Plus" size="small" />
          </ListRow.LeadingAsset>
          <ListRow.LeadingContent>
            <Text>Deposit from a bank account</Text>
          </ListRow.LeadingContent>
          <ListRow.TrailingContent>
            <Icon name="CaretRight" size="small" color="icon.weak" />
          </ListRow.TrailingContent>
        </ListRow>
      </SambaAutoLink>
      <SambaAutoLink
        to={transferSetupUrl}
        css={linkCss}
        onClick={() => {
          trackClick({ name: 'TransferIn', flow: 'FundAccount' });
        }}
      >
        <ListRow css={listRowCss}>
          <ListRow.LeadingAsset css={iconCss}>
            <Icon name="Transfer" size="small" />
          </ListRow.LeadingAsset>
          <ListRow.LeadingContent>
            <Text>Transfer from a Betterment account</Text>
          </ListRow.LeadingContent>
          <ListRow.TrailingContent>
            <Icon name="CaretRight" size="small" color="icon.weak" />
          </ListRow.TrailingContent>
        </ListRow>
      </SambaAutoLink>
      <SambaAutoLink to={accountOverviewUrl}>Skip for now</SambaAutoLink>
    </VStack>
  );
};

export const InitialFundingStep: React.FC<StepProps<{ accountId?: string | null }>> = ({ flowState }) => {
  const { accountId } = flowState.values;

  if (!accountId) {
    return <AutoNavigate to={urlBuilder.appHome.absolute()} />;
  }

  return (
    <WorkflowLayout
      feature="direct_trading_account_initial_funding"
      pageTitle="Fund your account"
      workflowTitle=""
      onClose={() => locationAssign(urlBuilder.appAccount.absolute({ accountId }))}
      size="medium"
    >
      <ErrorBoundary>
        <Content accountId={accountId} />
      </ErrorBoundary>
    </WorkflowLayout>
  );
};
